'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const getScrollContainer = () =>
      document.querySelector('main.content-layer') as HTMLElement | null;

    const handleScroll = () => {
      const container = getScrollContainer();
      const scrollTop = container ? container.scrollTop : window.scrollY;
      setScrolled(scrollTop > 40);
    };

    const container = getScrollContainer();
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
    } else {
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const handleNavClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    const hash = href.split('#')[1];
    if (!hash) {
      // „Startseite“ auf der Startseite: sanft nach ganz oben scrollen
      if (href === '/' && window.location.pathname === '/') {
        e.preventDefault();
        const top = document.querySelector('main.content-layer') as HTMLElement | null;
        if (top) top.scrollTo({ top: 0, behavior: 'smooth' });
        else window.scrollTo({ top: 0, behavior: 'smooth' });
        if (window.location.hash) history.replaceState(null, '', '/');
      }
      return;
    }
    const target = document.getElementById(hash);
    const container = document.querySelector('main.content-layer') as HTMLElement | null;
    if (target && container) {
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const navLinks = [
    { label: 'Startseite', href: '/' },
    { label: 'Fakten', href: '/#fakten' },
    { label: 'Lösung', href: '/#loesung' },
    { label: 'Mehrwert', href: '/#mehrwert' },
    { label: 'Über uns', href: '/#ueber-uns' },
    { label: 'Ablauf', href: '/#ansatz' },
    { label: 'Preise', href: '/#preise' },
    { label: 'Kontakt', href: '/#kontakt' },
    { label: 'FAQ', href: '/#faq' },
    { label: 'Blog', href: '/#blog' },
  ];

  return (
    <header
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        zIndex: 9999,
        transition: 'all 0.5s ease',
        background: scrolled
          ? 'rgba(3, 4, 12, 0.55)'
          : 'linear-gradient(to bottom, rgba(3,4,12,0.25) 0%, transparent 100%)',
        backdropFilter: scrolled ? 'blur(20px) saturate(1.2)' : 'blur(0px)',
        WebkitBackdropFilter: scrolled ? 'blur(20px) saturate(1.2)' : 'blur(0px)',
        borderBottom: scrolled ? '1px solid rgba(99, 102, 241, 0.15)' : '1px solid transparent',
        boxShadow: scrolled ? '0 4px 40px rgba(0,0,0,0.6), 0 0 60px rgba(37,99,235,0.04)' : 'none',
      }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-5 lg:px-8 py-4 sm:py-5 flex items-center justify-between">
        {/* Logo */}
        <Link
          href="/"
          onClick={(e) => {
            // Auf der Startseite: sanft nach ganz oben scrollen (ein normaler Link tut dort nichts)
            if (window.location.pathname === '/') {
              e.preventDefault();
              const container = document.querySelector('main.content-layer') as HTMLElement | null;
              if (container) container.scrollTo({ top: 0, behavior: 'smooth' });
              else window.scrollTo({ top: 0, behavior: 'smooth' });
              if (window.location.hash) history.replaceState(null, '', '/');
              setMenuOpen(false);
            }
          }}
          className="flex items-center gap-2.5 group flex-shrink-0"
        >
          {/* Galaxy icon */}
          <div
            className="relative flex items-center justify-center"
            style={{ width: 40, height: 40 }}
          >
            <svg width="40" height="40" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg" className="-ml-2.5">
              {/* Outer ring */}
              <circle cx="17" cy="17" r="15.5" stroke="rgba(99,102,241,0.35)" strokeWidth="0.8"/>
              {/* Galaxy spiral arms */}
              <path
                d="M17 17 C20 12, 26 10, 28 14 C30 18, 26 24, 20 24 C14 24, 8 20, 8 14 C8 10, 12 8, 17 10"
                stroke="url(#galaxyGrad1)"
                strokeWidth="1.2"
                strokeLinecap="round"
                fill="none"
                opacity="0.7"
              />
              <path
                d="M17 17 C14 22, 8 24, 6 20 C4 16, 8 10, 14 10 C20 10, 26 14, 26 20 C26 24, 22 26, 17 24"
                stroke="url(#galaxyGrad2)"
                strokeWidth="1.2"
                strokeLinecap="round"
                fill="none"
                opacity="0.5"
              />
              {/* Center core glow */}
              <circle cx="17" cy="17" r="2.5" fill="url(#coreGrad)" />
              <circle cx="17" cy="17" r="1.2" fill="white" opacity="0.9"/>
              {/* Star dots */}
              <circle cx="24" cy="11" r="0.8" fill="#93C5FD" opacity="0.8"/>
              <circle cx="10" cy="23" r="0.7" fill="#A5B4FC" opacity="0.7"/>
              <circle cx="26" cy="21" r="0.6" fill="#60A5FA" opacity="0.6"/>
              <circle cx="9" cy="12" r="0.6" fill="#C7D2FE" opacity="0.6"/>
              <circle cx="20" cy="7" r="0.5" fill="#93C5FD" opacity="0.5"/>
              <defs>
                <linearGradient id="galaxyGrad1" x1="8" y1="8" x2="28" y2="24" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#60A5FA"/>
                  <stop offset="100%" stopColor="#818CF8"/>
                </linearGradient>
                <linearGradient id="galaxyGrad2" x1="28" y1="24" x2="8" y2="8" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#818CF8"/>
                  <stop offset="100%" stopColor="#38BDF8"/>
                </linearGradient>
                <radialGradient id="coreGrad" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#E0F2FE"/>
                  <stop offset="60%" stopColor="#60A5FA"/>
                  <stop offset="100%" stopColor="#4338CA" stopOpacity="0"/>
                </radialGradient>
              </defs>
            </svg>
            {/* Subtle glow behind icon */}
            <div
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                background: 'radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%)',
                filter: 'blur(4px)',
              }}
            />
          </div>
          {/* Wordmark */}
          <span
            className="font-display tracking-tight text-white leading-none -ml-2.5"
            style={{ fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.03em' }}
          >
            in
            <span
              style={{
                background: 'linear-gradient(135deg, #60A5FA 0%, #818CF8 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              .
            </span>
            versum
          </span>
        </Link>

        {/* Desktop Nav — gap scales up gradually with viewport width to avoid crowding on tablets like iPad landscape */}
        <nav className="hidden md:flex items-center gap-x-3 lg:gap-x-4 xl:gap-x-6">
          {navLinks?.map((link) => (
            <a
              key={link?.label}
              href={link?.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="text-sm font-medium text-text-secondary hover:text-white transition-colors duration-200 tracking-tight whitespace-nowrap"
            >
              {link?.label}
            </a>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:block flex-shrink-0">
          <a
            href="/#kontakt"
            onClick={(e) => handleNavClick(e, '/#kontakt')}
            className="btn-primary py-2.5 px-5 text-xs"
          >
            Kontakt
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-white p-2 -mr-1"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menü öffnen"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            {menuOpen ? (
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round"/>
            ) : (
              <>
                <line x1="3" y1="7" x2="21" y2="7"/>
                <line x1="3" y1="12" x2="21" y2="12"/>
                <line x1="3" y1="17" x2="21" y2="17"/>
              </>
            )}
          </svg>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div
          className="md:hidden border-t"
          style={{
            background: 'rgba(3,4,12,0.96)',
            backdropFilter: 'blur(32px)',
            borderColor: 'rgba(99,102,241,0.15)',
          }}
        >
          <div className="flex flex-col px-5 py-4 gap-0">
            {navLinks?.map((link) => (
              <a
                key={link?.label}
                href={link?.href}
                onClick={(e) => { handleNavClick(e, link.href); setMenuOpen(false); }}
                className="text-base font-medium text-text-secondary hover:text-white transition-colors border-b"
                style={{
                  borderColor: 'rgba(99,102,241,0.08)',
                  paddingTop: '0.875rem',
                  paddingBottom: '0.875rem',
                }}
              >
                {link?.label}
              </a>
            ))}
            <a
              href="/#kontakt"
              className="btn-primary justify-center mt-5"
              onClick={(e) => { handleNavClick(e, '/#kontakt'); setMenuOpen(false); }}
            >
              Angebot anfragen
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
